import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  MenuItem,
  Modal,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";

import { useEffect } from "react";
import { useSuperAdminOrganization } from "../../../context/SuperAdmin/OrganizationContext";
import { PutOrgFields } from "../../../interfaces/SuperAdmin/Pages/iDataOrganizacion";
import { Estado } from "../../../interfaces/SuperAdmin/Others/iDataEstado";

const EditOrgModal = () => {
  const {
    isLoadingPaises,
    toggleEditModal,
    isEditing,
    paises,
    tiposPlan,
    isEditModalOpen,
    selectedOrg,
    orgEdit,
    tiposObjetivo,
  } = useSuperAdminOrganization();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<PutOrgFields>();

  // Función para manejar el envío del formulario
  const handleSubmitForm = (data: PutOrgFields) => {
    if (selectedOrg) {
      console.log(data);

      orgEdit(selectedOrg.id_organizacion, data);
    }
  };

  useEffect(() => {
    if (selectedOrg) {
      const { nombre, estado, id_pais, id_tipo_plan, tipo_objetivo } =
        selectedOrg;
      reset({ nombre, id_pais, id_tipo_plan, estado, tipo_objetivo });
    }
  }, [selectedOrg]);

  const handleClose = () => {
    if (!isEditing) {
      reset();
      toggleEditModal(false);
    }
  };

  return (
    <Modal
      open={isEditModalOpen}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.3)", // Fondo negro con opacidad
      }}
    >
      <Box
        component="form"
        sx={{
          width: 400,
          bgcolor: "background.paper",
          borderRadius: 2,
          p: 4,
          boxShadow: 24,
          minWidth: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
        onSubmit={handleSubmit(handleSubmitForm)}
      >
        {isLoadingPaises ? (
          <Typography
            id="edit-subscription-modal-title"
            justifyContent={"center"}
            sx={{
              width: 1,
              textAlign: "center",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Cargando...
          </Typography>
        ) : (
          <>
            <Typography variant="h6" component="h2">
              Editar organizacion
            </Typography>

            {/* Campo Nombre */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Nombre de la organización
              </Typography>
              <Controller
                name="nombre"
                control={control}
                rules={{ required: "El nombre es obligatorio" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    margin="normal"
                    error={!!errors.nombre}
                    helperText={errors.nombre?.message}
                    disabled={isEditing}
                  />
                )}
              />
            </Box>

            {/* Campo Pais */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                País
              </Typography>
              <FormControl error={!!errors.id_pais}>
                <Controller
                  name="id_pais"
                  control={control}
                  rules={{ required: "El país es obligatorio" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      fullWidth
                      disabled={isEditing}
                    >
                      {paises.map((pais) => (
                        <MenuItem key={pais.id_pais} value={pais.id_pais}>
                          {pais.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.id_pais?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* Campo Tipo plan */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Tipo de plan
              </Typography>
              <FormControl error={!!errors.id_tipo_plan}>
                <Controller
                  name="id_tipo_plan"
                  control={control}
                  rules={{ required: "El tipo de plan es obligatorio" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      fullWidth
                      disabled={isEditing}
                    >
                      {tiposPlan.map((plan) => (
                        <MenuItem
                          key={plan.id_tipo_plan}
                          value={plan.id_tipo_plan}
                        >
                          {plan.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.id_tipo_plan?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* Campo Tipos de OAS */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Tipos de OAS
              </Typography>
              <FormControl
                component="fieldset"
                error={!!errors.tipo_objetivo}
                sx={{ mt: 1 }}
              >
                <Controller
                  name="tipo_objetivo"
                  control={control}
                  rules={{
                    validate: (value) =>
                      (value && value.length > 0) ||
                      "Debes seleccionar al menos un tipo de OAS",
                  }}
                  render={({ field }) => (
                    <FormGroup>
                      {tiposObjetivo.map((tipo) => (
                        <FormControlLabel
                          key={tipo.id_tipo_objetivo}
                          control={
                            <Checkbox
                              checked={
                                field.value?.includes(tipo.id_tipo_objetivo) ||
                                false
                              }
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [
                                      ...(field.value || []),
                                      tipo.id_tipo_objetivo,
                                    ]
                                  : (field.value || []).filter(
                                      (v: string) => v !== tipo.id_tipo_objetivo
                                    );
                                field.onChange(newValue);
                              }}
                              disabled={isEditing}
                              sx={{
                                color: "#336B87",
                                "&.Mui-checked": {
                                  color: "#336B87",
                                },
                              }}
                            />
                          }
                          label={tipo.nombre}
                        />
                      ))}
                    </FormGroup>
                  )}
                />
                <FormHelperText>{errors.tipo_objetivo?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* Switch de Estado */}
            <Typography variant="body1" sx={{ mt: 3, fontWeight: 500 }}>
              Estado
            </Typography>
            <Controller
              name="estado"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value === Estado.ACTIVO}
                      onChange={(e) =>
                        field.onChange(
                          e.target.checked ? Estado.ACTIVO : Estado.INACTIVO
                        )
                      }
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#2E7D32",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#2E7D32",
                          },
                      }}
                    />
                  }
                  label={field.value === Estado.ACTIVO ? "Activo" : "Inactivo"}
                />
              )}
            />

            <Box sx={{ mt: 3, textAlign: "right" }}>
              <Button
                variant="contained"
                sx={{ background: "#336B87" }}
                disabled={isEditing}
                type="submit"
              >
                {isEditing ? "Guardando" : "Guardar"}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default EditOrgModal;
